import { useEffect, useMemo, useState } from 'react';
import { Link2, Loader2, Search, Crown, RefreshCw } from 'lucide-react';
import { listAdminReferrals, type AdminReferralRow } from '@/shared/services/referralsService';

export default function ReferralsPage() {
  const [rows, setRows] = useState<AdminReferralRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');

  const load = () => {
    setLoading(true);
    setError(null);
    listAdminReferrals()
      .then(setRows)
      .catch((e: any) => setError(e?.detail || e?.message || 'Erro ao carregar'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r =>
      (r.accountId || '').toLowerCase().includes(q) ||
      r.code.toLowerCase().includes(q) ||
      (r.referredByCode || '').toLowerCase().includes(q) ||
      String(r.userNum).includes(q),
    );
  }, [rows, query]);

  const totalReferred = useMemo(() => rows.reduce((s, r) => s + (r.referredCount ?? 0), 0), [rows]);

  return (
    <div>
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-3">
            <Link2 className="text-[#ca69d3]" size={26} />
            Referências
          </h1>
          <p className="text-sm text-white/65 mt-1">
            Contas com código de referência + quantos cadastros cada uma trouxe.
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold
                     bg-amber-500/10 border border-amber-500/30 text-amber-400
                     hover:bg-amber-500/20 transition cursor-pointer disabled:opacity-50"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          Atualizar
        </button>
      </div>

      {/* Resumo */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <SummaryCard label="Contas com código" value={rows.length.toLocaleString('pt-BR')} color="#ca69d3" />
        <SummaryCard label="Total de referidos" value={totalReferred.toLocaleString('pt-BR')} color="#00e1d7" />
        <SummaryCard
          label="Top referenciador"
          value={rows[0] ? `${rows[0].accountId || `User ${rows[0].userNum}`} (${rows[0].referredCount})` : '—'}
          color="#fbbf24"
        />
      </div>

      <div className="surface-card rounded-xl p-5">
        <div className="relative mb-4">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar por conta, código ou UserNum..."
            className="w-full bg-[#0d141b] border border-white/[0.08] rounded-lg pl-9 pr-3 py-2
                       text-sm text-white placeholder:text-white/40
                       focus:outline-none focus:border-[#00e1d7]/40"
          />
        </div>

        {error && (
          <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-3 mb-3 text-sm text-red-400">
            {error}
          </div>
        )}

        {loading ? (
          <div className="py-12 flex justify-center"><Loader2 className="animate-spin text-white/40" /></div>
        ) : filtered.length === 0 ? (
          <p className="text-xs text-white/40 py-8 text-center">
            {rows.length === 0 ? 'Nenhuma conta com código gerado ainda.' : 'Nenhum resultado pra esse filtro.'}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/[0.08] text-[10px] font-bold uppercase tracking-wider text-white/55">
                  <th className="text-left py-2 px-3">#</th>
                  <th className="text-left py-2 px-3">Conta</th>
                  <th className="text-left py-2 px-3">Código</th>
                  <th className="text-center py-2 px-3">Referidos</th>
                  <th className="text-left py-2 px-3">Veio de</th>
                  <th className="text-left py-2 px-3">Criado em</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, i) => (
                  <tr key={r.userNum} className="border-b border-white/[0.04] last:border-0 hover:bg-white/[0.02]">
                    <td className="py-2.5 px-3 text-white/45 font-mono text-xs">{i + 1}</td>
                    <td className="py-2.5 px-3">
                      <div>
                        <p className="text-white font-semibold">{r.accountId || `User ${r.userNum}`}</p>
                        <p className="text-[10px] text-white/40 font-mono">UserNum {r.userNum}</p>
                      </div>
                    </td>
                    <td className="py-2.5 px-3">
                      <span className="inline-block px-2 py-1 rounded-md font-mono text-xs font-bold
                                       bg-fuchsia-500/15 text-fuchsia-300 border border-fuchsia-500/30">
                        {r.code}
                      </span>
                    </td>
                    <td className="py-2.5 px-3 text-center">
                      <span className={`inline-flex items-center gap-1 font-bold ${(r.referredCount ?? 0) > 0 ? 'text-[#00e1d7]' : 'text-white/40'}`}>
                        {(r.referredCount ?? 0) > 0 && i === 0 && <Crown size={12} className="text-amber-400" />}
                        {(r.referredCount ?? 0).toLocaleString('pt-BR')}
                      </span>
                    </td>
                    <td className="py-2.5 px-3 font-mono text-xs text-white/65">
                      {r.referredByCode || '—'}
                    </td>
                    <td className="py-2.5 px-3 text-xs text-white/55">
                      {r.createdAt ? new Date(r.createdAt).toLocaleString('pt-BR') : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function SummaryCard({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <div className="stat-widget" style={{ ['--c' as any]: color }}>
      <div className="relative z-10">
        <p className="text-[11px] font-bold text-white/75 uppercase tracking-wider">{label}</p>
        <p className="text-2xl font-black text-white mt-1 leading-none truncate">{value}</p>
      </div>
    </div>
  );
}
